import * as React from "react";
import { makeStyles } from '@material-ui/core/styles';
import Medewerker, { IMinimalMedewerker } from "../../../entities/Medewerker";
import { MultiSelect, IOption } from "../../atoms/forms/MultiSelect";
import { ICommonMultiSelectControls } from "./ICommonInputsControls";
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { IApplicationState } from "../../../store";
import * as Selectie from "../../../store/selectie";
import { FetchState } from "../../../entities/FetchState";

// All the incoming values
interface IProps {
    showOnlyActive?: boolean; // New prop to filter for active medewerkers only
    syncWithRedux?: boolean; // Optional: automatically connect to Redux state
}

// Outgoing functions
interface IDispatch {

}

interface IMedewerker extends IOption {
    medewerker: IMinimalMedewerker;
}
const mapOptionsToIMedewerker = (medewerker: IMinimalMedewerker): IMedewerker => {
    return {
        value: medewerker.streamId,
        label: medewerker.fullName,
        medewerker: medewerker
    }
}

const mapOrCreateOptionToIMedewerker = (medewerker: IMinimalMedewerker, medewerkers: IMedewerker[]): IMedewerker => {
    const foundMedewerker = medewerkers.find(m => m.medewerker.id === medewerker.id);
    if (foundMedewerker) {
        return foundMedewerker;
    }
    // If not found, return a new object with the same structure
    
    return {
        value: medewerker.streamId,
        label: medewerker.fullName,
        medewerker: Medewerker.expandMiniMedewerker(medewerker)
    }
}

// Legacy props interface for backward compatibility
interface ILegacyProps extends Omit<ICommonMultiSelectControls<IMinimalMedewerker>, 'options'> {
    options: IMinimalMedewerker[];
}

// New props interface for Redux integration
interface IReduxProps extends Omit<ICommonMultiSelectControls<IMinimalMedewerker>, 'options'> {
    options?: never; // Options will come from Redux
}

// Actual Component
export type AllProps = IProps & IDispatch & (ILegacyProps | IReduxProps);
export const MedewerkersMultiSelect = (props: AllProps) => {
    const { t: tDatatypes } = useTranslation('datatypes');
    
    // Redux state and dispatch - handle case where Redux is not available (e.g., in Storybook)
    const medewerkersList = useSelector((state: IApplicationState) => state?.selectie?.allMedewerkersNoAll) || {
        fetchState: FetchState.NotFetched,
        errorMessage: "",
        fetchTime: new Date(),
        medewerkers: []
    };
    const dispatch = useDispatch();
    
    // Determine if we should use Redux or passed options
    const useRedux = props.syncWithRedux !== false && !('options' in props && props.options);
    
    // Fetch medewerkers if using Redux and not loaded
    React.useEffect(() => {
        if (useRedux && medewerkersList.fetchState === FetchState.NotFetched && dispatch) {
            try {
                dispatch(Selectie.SelectieActionCreator.FetchAllMedewerkers());
            } catch (error) {
                console.warn('Redux dispatch failed - likely running in isolation (Storybook)', error);
            }
        }
    }, [dispatch, medewerkersList.fetchState, useRedux]);
    
    // Get options from Redux or props, with optional filtering
    const getFilteredOptions = React.useMemo(() => {
        let options: IMinimalMedewerker[];
        
        if (useRedux && medewerkersList.medewerkers && medewerkersList.medewerkers.length > 0) {
            // Redux store contains full Medewerker objects, filter them first if needed
            let fullMedewerkers = medewerkersList.medewerkers;
            if (props.showOnlyActive) {
                fullMedewerkers = fullMedewerkers.filter(m => m.isActive);
            }
            options = fullMedewerkers;
        } else {
            // Fallback to props options or empty array
            options = ('options' in props && props.options) ? props.options : [];
        }
        
        return options;
    }, [useRedux, medewerkersList.medewerkers, props.showOnlyActive, props.options]);

    const medewerkers = React.useMemo(() => 
        getFilteredOptions.map(mapOptionsToIMedewerker), 
        [getFilteredOptions]
    );

    // Convert props.value to the internal format, but don't store it in state
    const selectedMedewerkers = React.useMemo(() => {
        if (!props.value || props.value.length === 0) {
            return [];
        }
        return props.value.map(m => mapOrCreateOptionToIMedewerker(m, medewerkers));
    }, [props.value, medewerkers]);

    // Determine loading state - handle case where medewerkersList might be undefined
    const isLoading = props.loading || (useRedux && medewerkersList?.fetchState === FetchState.Busy);

    return (<MultiSelect
        {...props}
        label={props.label || tDatatypes('medewerkersLabel') || "Medewerkers"}
        placeholder={tDatatypes('medewerkersPlaceholder')}
        loading={isLoading}

        options={medewerkers}
        value={selectedMedewerkers}

        onChange={(value, isValid) => {
            props.onChange && props.onChange(value?.map(v => v.medewerker) || [], isValid);
        }}
        onValidate={(value) => value && props.onValidate ? props.onValidate(value.map(v => v.medewerker)) : [false, ""]} 
    />)
}

// Convenience components for common use cases
export const ActiveMedewerkersMultiSelect = (props: Omit<AllProps, 'showOnlyActive' | 'syncWithRedux'>) => {
    return <MedewerkersMultiSelect {...props} showOnlyActive={true} syncWithRedux={true} />;
};

export const AllMedewerkersMultiSelect = (props: Omit<AllProps, 'showOnlyActive' | 'syncWithRedux'>) => {
    return <MedewerkersMultiSelect {...props} showOnlyActive={false} syncWithRedux={true} />;
};

// CSS
const useStyles = makeStyles(() => ({ }));
